import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { decode as decodePng } from 'fast-png';
import GradedCanvas from '../components/GradedCanvas';
import GradePanel from '../components/GradePanel';
import ColorWorkflow from '../components/ColorWorkflow';
import Scopes from '../components/Scopes';
import SplitPreview from '../components/SplitPreview';
import CropBox from '../components/CropBox';
import MaskBox from '../components/MaskBox';
import MaskPanel from '../components/MaskPanel';
import HealBox from '../components/HealBox';
import ColorAssistant from '../components/ColorAssistant';
import StageAssist from '../components/StageAssist';
import GradingConfigPanel from '../components/GradingConfigPanel';
import { loadImageEl, autoAlignCrop } from '../utils/colorMatch';
import {
  useAsset,
  useGradePresets,
  addGradePreset,
  deleteGradePreset,
  updateAssetGrade,
  updateAssetAiGrade,
  updateAssetCrop,
  requestPhotoGrade,
  requestDevelop,
  createStillGrabAsset,
} from '../data/firestore';
import { canReviseAsset } from '../utils/nlRevise';
import { DEFAULT_GRADE, DEFAULT_MASK_ADJ, DEFAULT_MASK_RANGE, clone, gradeSignature, gradeLayer, baseGrade, tameProofColor, lutFromCubeText, activeAdjustments } from '../utils/grade';
import { STAGES, STAGE_QUESTIONS, PHASES, phaseOfStage, gradeUpToStage, computeStageSuggestions, stageSuggestionsByPath, applyPathSuggestions, hasAutoSuggestions } from '../utils/gradeStages';
import { captureCanvasFrame, measurementLines } from '../utils/stageAssist';
import { CROP_ASPECTS, DEFAULT_CROP_RECT, rectForAR, rectScale, rectAtScale, normalizeCrop, zoomFromScale, scaleFromZoom, framingToCrop } from '../utils/crop';
import { useAgentHandlers, useAgentContext } from '../agent/agentBus';
import { resolveLook, applyLookGrade, adjustGrade, presetNames } from '../agent/colorActions';
import firebase from '../firebaseConfig';

const API = process.env.REACT_APP_UPLOAD_API || '';

// The stage-by-stage grading pipeline (STAGES), per-stage starter questions,
// phases, cumulative-grade + suggestion helpers now live in utils/gradeStages —
// shared with the movie color studio so clips get the same guided workflow.

// Render an AI answer's light markdown (**bold** + "- " bullets) without pulling
// in a markdown lib. Groups consecutive bullets into a list; everything else is a
// paragraph; inline **bold** becomes <strong>.
function inlineBold(text) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((seg, i) => (
    seg.startsWith('**') && seg.endsWith('**')
      ? <strong key={i}>{seg.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  ));
}
function AnswerText({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let bullets = null;
  const flush = () => { if (bullets) { blocks.push({ type: 'ul', items: bullets }); bullets = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const m = line.match(/^\s*[-•*]\s+(.*)$/);
    if (m) { (bullets || (bullets = [])).push(m[1]); continue; }
    flush();
    if (line.trim()) blocks.push({ type: 'p', text: line });
  }
  flush();
  return (
    <>
      {blocks.map((b, i) => (b.type === 'ul' ? (
        <ul key={i} className="cs-ask-ul">{b.items.map((it, k) => <li key={k}>{inlineBold(it)}</li>)}</ul>
      ) : (
        <p key={i} className="cs-ask-p">{inlineBold(b.text)}</p>
      )))}
    </>
  );
}

// Delivery formats the editor can bake. `magazine` is also the on-screen display
// image used for the fullscreen view + the magazine spread.
const EXPORT_OPTIONS = [
  { id: 'magazine', label: 'Magazine JPEG', hint: 'sRGB · long edge 3000 · Q90' },
  { id: 'fulljpeg', label: 'Full-res JPEG', hint: 'sRGB · max quality' },
  { id: 'tiff16', label: '16-bit TIFF', hint: 'archive · print · Lightroom' },
  { id: 'png8', label: 'PNG', hint: 'lossless 8-bit' },
  { id: 'webp', label: 'WebP', hint: 'smaller web file' },
];

export default function PhotoEditor() {
  const { tripId, assetId } = useParams();
  const navigate = useNavigate();
  const { doc: asset } = useAsset(tripId, assetId);
  const { docs: presets } = useGradePresets();

  const [grade, setGrade] = useState(() => clone(DEFAULT_GRADE));
  const [source, setSource] = useState(null);
  const [stats, setStats] = useState(null);
  const [beforeStats, setBeforeStats] = useState(null); // histogram of the stage's INPUT (left pane), powers auto suggestions
  const [compare, setCompare] = useState('stage'); // stage | off(single) | ba | stack
  const [stageIdx, setStageIdx] = useState(0); // index into STAGES for the stage-by-stage workflow
  const [aiGrade, setAiGrade] = useState(null); // durable snapshot of the model's baked grade (reference/target)
  const [showAi, setShowAi] = useState(false); // preview the full AI auto-grade full-frame
  const [stageTarget, setStageTarget] = useState('prev'); // stage compare left pane: 'prev' step vs 'ai' grade
  // Per-stage "Ask the AI" teaching Q&A (returns prose, not grade ops).
  const [askInput, setAskInput] = useState('');
  const [askBusy, setAskBusy] = useState(false);
  const [askThread, setAskThread] = useState([]); // [{ role:'user'|'assistant', text }]
  const [askErr, setAskErr] = useState('');
  const [askOpen, setAskOpen] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [fmts, setFmts] = useState({ magazine: true, fulljpeg: true, tiff16: false, png8: false, webp: false });
  const [exportOpen, setExportOpen] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [crop, setCrop] = useState(null); // non-destructive {x,y,w,h} or null
  const [cropMode, setCropMode] = useState(false);
  const [aspectId, setAspectId] = useState('free');
  const [maxPreview, setMaxPreview] = useState(false); // float the preview fullscreen on top
  const [maskMode, setMaskMode] = useState(false); // show mask handles on the image
  const [activeMaskId, setActiveMaskId] = useState(null);
  const [healMode, setHealMode] = useState(false); // show heal/clone spot handles
  const [activeHealId, setActiveHealId] = useState(null);
  const [reference, setReference] = useState(null); // { dataUrl, name } target to match
  const [refOpacity, setRefOpacity] = useState(0.5); // align overlay opacity
  const [refDiff, setRefDiff] = useState(false); // difference blend for alignment
  const [autoAligning, setAutoAligning] = useState(false);
  const [busyAi, setBusyAi] = useState(null); // 'sky' | 'subject' while detecting
  const [hasClip, setHasClip] = useState(() => {
    try { return !!localStorage.getItem('cs-look-clipboard'); } catch { return false; }
  });
  const [copied, setCopied] = useState(false);
  const [lutErr, setLutErr] = useState('');
  const stageRef = useRef(null);
  const lutInputRef = useRef(null);
  const refInputRef = useRef(null); // hidden file input for the match reference
  const hydrated = useRef(false);
  const saveTimer = useRef(null);
  const cropTimer = useRef(null);
  const cropBaseline = useRef(null); // crop snapshot when crop mode was opened (for Cancel)

  // Reset the Q&A thread whenever the active stage changes — questions are
  // stage-scoped. Kept above the early returns so the hook order stays stable.
  useEffect(() => { setAskThread([]); setAskErr(''); }, [stageIdx]);

  const develop = asset?.develop || {};
  const gradeRender = asset?.gradeRender || {};
  const proxyUrl = asset?.proxy16Url || '';
  const lineage = asset?.lineage || {};

  // Hydrate the grade + crop from the asset once. We keep a durable snapshot of
  // the model's baked grade (`aiGrade`) so it can always be viewed/restored as a
  // reference. On the VERY FIRST open of a photo we also reset the working grade
  // to the decoded negative so the guided workflow builds up from scratch; on
  // later opens the in-progress rework is resumed (no data loss).
  useEffect(() => {
    if (asset && !hydrated.current) {
      hydrated.current = true;
      const baked = asset.grade ? clone(asset.grade) : clone(DEFAULT_GRADE);
      // The AI's LOCAL masks (water teal + subject lift) should be visible and
      // editable in the workbench's Local stage, not hidden. Pull the tamed
      // versions out of whichever grade holds them so the stage is populated by
      // default — the user can adjust or remove them there.
      const isAutoMask = (m) => m && typeof m.source === 'string' && m.source.startsWith('auto-');
      const autoMasksFrom = (gr) => (gr ? (tameProofColor(gr).masks || []).filter(isAutoMask) : []);
      const withAutoMasks = (g, am) => {
        if (!am.length) return g;
        const userMasks = (g.masks || []).filter((m) => !isAutoMask(m));
        return { ...g, masks: [...userMasks, ...clone(am)] };
      };
      if (asset.aiGrade) {
        // Returning visit — keep the reference and resume the saved working grade.
        setAiGrade(clone(asset.aiGrade));
        const hasAuto = (baked.masks || []).some(isAutoMask);
        setGrade(hasAuto ? baked : withAutoMasks(baked, autoMasksFrom(asset.aiGrade)));
      } else {
        // First visit — snapshot the AI result, start from the negative, but keep
        // the AI's local masks so they show up in the Local stage.
        setAiGrade(baked);
        updateAssetAiGrade(tripId, assetId, baked).catch(() => {});
        const neg = withAutoMasks(baseGrade(baked), autoMasksFrom(baked));
        setGrade(neg);
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
          updateAssetGrade(tripId, assetId, neg).catch(() => {});
        }, 700);
      }
      if (asset.crop && asset.crop.w > 0 && asset.crop.h > 0) {
        setCrop(asset.crop);
        setAspectId('free');
      }
    }
  }, [asset, tripId, assetId]);

  // Load the developed 16-bit proxy as a float source for the WebGL canvas.
  useEffect(() => {
    let on = true;
    setSource(null);
    setLoadErr('');
    if (!proxyUrl) return undefined;
    (async () => {
      try {
        const res = await fetch(proxyUrl, { mode: 'cors' });
        if (!res.ok) throw new Error(`proxy ${res.status}`);
        const buf = new Uint8Array(await res.arrayBuffer());
        const png = decodePng(buf);
        if (!on) return;
        setSource({ data: png.data, width: png.width, height: png.height, channels: png.channels });
      } catch (err) {
        if (on) setLoadErr(String(err.message || err));
      }
    })();
    return () => {
      on = false;
    };
  }, [proxyUrl]);

  // Auto-prep on open: a frame-grab / bake photo already HAS its 16-bit negative
  // in S3 (a.s3Key), it just needs the CORS-friendly editor proxy built from it.
  // Don't make the user click "Prepare for editing" — kick the develop the moment
  // the editor opens so pressing Edit goes straight into building from the existing
  // negative. Fires once per asset; skips RAW (heavier develop, already auto-queued
  // at upload), errored develops (show the manual retry), and anything already
  // ready/in-flight. The "building a 16-bit proxy…" progress still shows briefly.
  const autoPrepReq = useRef(false);
  useEffect(() => {
    if (!asset || autoPrepReq.current) return;
    const ds = asset.develop?.status;
    const ready = ds === 'ready' && !!asset.proxy16Url;
    const active = ds === 'queued' || ds === 'developing';
    if (ready || active || ds === 'error' || asset.isRaw || !asset.s3Key) return;
    autoPrepReq.current = true;
    requestDevelop(tripId, assetId).catch(() => {});
  }, [asset, tripId, assetId]);

  // Undo / redo history for the working grade. Every grade mutation routes
  // through onChange, so we snapshot the pre-change grade here. Rapid slider
  // drags are coalesced into a single history entry (one undo per gesture);
  // discrete actions like "Apply" a suggestion each get their own entry.
  const histRef = useRef({ past: [], future: [], lastT: 0 });
  const [, setHistTick] = useState(0);
  const bumpHist = () => setHistTick((t) => t + 1);
  const HIST_MAX = 100;

  const persistGrade = (g, delay = 700) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateAssetGrade(tripId, assetId, g).catch(() => {});
    }, delay);
  };

  const onChange = (next) => {
    const h = histRef.current;
    const now = Date.now();
    // Coalesce a burst of changes (e.g. dragging a slider) into one undo step.
    if (now - h.lastT > 450) {
      h.past.push(grade);
      if (h.past.length > HIST_MAX) h.past.shift();
      h.future = [];
      bumpHist();
    }
    h.lastT = now;
    setGrade(next);
    persistGrade(next);
  };

  const undo = () => {
    const h = histRef.current;
    if (!h.past.length) return;
    const prev = h.past.pop();
    h.future.push(grade);
    h.lastT = 0; // next edit starts a fresh history entry
    bumpHist();
    setGrade(prev);
    persistGrade(prev, 300);
  };

  const redo = () => {
    const h = histRef.current;
    if (!h.future.length) return;
    const nextG = h.future.pop();
    h.past.push(grade);
    h.lastT = 0;
    bumpHist();
    setGrade(nextG);
    persistGrade(nextG, 300);
  };

  const canUndo = histRef.current.past.length > 0;
  const canRedo = histRef.current.future.length > 0;

  // Keyboard: ⌘/Ctrl+Z to undo, ⌘/Ctrl+Shift+Z (or Ctrl+Y) to redo. Skip while
  // typing in a field so it doesn't fight native text undo.
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redoRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Esc exits the maximized (floated) preview.
  useEffect(() => {
    if (!maxPreview) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') setMaxPreview(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [maxPreview]);

  // Persist the crop (debounced) — applied to display derivatives + every export
  // by the bake worker; the master negative stays full-frame so it can be
  // re-cropped any time.
  const scheduleSaveCrop = (next) => {
    if (cropTimer.current) clearTimeout(cropTimer.current);
    cropTimer.current = setTimeout(() => updateAssetCrop(tripId, assetId, next).catch(() => {}), 600);
  };
  // Keep flip/straighten geometry attached when the box rect changes.
  const withGeo = (rect) => (rect
    ? { ...rect, rotate: crop?.rotate || 0, flipH: !!crop?.flipH, flipV: !!crop?.flipV }
    : rect);
  const onCropChange = (next) => {
    const merged = withGeo(next);
    setCrop(merged);
    scheduleSaveCrop(merged);
  };
  const srcAR = source && source.width && source.height
    ? source.width / source.height
    : (asset?.width && asset?.height ? asset.width / asset.height : 0);
  const aspect = CROP_ASPECTS.find((a) => a.id === aspectId) || CROP_ASPECTS[0];
  // 'source' resolves to the live source aspect ratio; lockRatio is the box's
  // normalized w/h (1 means it matches the original frame shape).
  const aspectR = aspect.id === 'source' ? srcAR : aspect.r;
  const lockRatio = aspectR && srcAR ? aspectR / srcAR : null;
  const chooseAspect = (id) => {
    setAspectId(id);
    const targetAR = id === 'source' ? srcAR : (CROP_ASPECTS.find((x) => x.id === id) || {}).r;
    const rect = !targetAR
      ? (crop && crop.w > 0 ? crop : { ...DEFAULT_CROP_RECT }) // free → keep/seed a box
      : (rectForAR(targetAR, 0.85, srcAR) || { ...DEFAULT_CROP_RECT });
    const next = withGeo(rect);
    setCrop(next);
    scheduleSaveCrop(next);
  };
  // Apply a natural-language reframe ("zoom in on the barrel ~2x") from the color
  // assistant: turn its framing intent into a crop (clamped to a safe zoom for
  // the source, composed over any current crop) and set it live. Returns true if
  // a crop was applied.
  const applyReframe = (framing) => {
    if (!framing || !srcAR) return false;
    const displayedAR = crop && crop.w > 0 ? (crop.w / crop.h) * srcAR : srcAR;
    const redSource = !!(asset?.lineage?.sourceIsRaw || asset?.isRaw);
    const fr = framingToCrop(framing, { displayedAR, existingCrop: crop || null, redSource });
    if (!fr.crop) return false;
    const next = withGeo(fr.crop);
    setCrop(next);
    setAspectId(framing.aspect && framing.aspect !== 'source' ? framing.aspect : 'free');
    scheduleSaveCrop(next);
    return true;
  };
  // Persist a crop immediately (skip the debounce) — used by Remove / Cancel so
  // the doc reflects the action right away.
  const saveCropNow = (next) => {
    if (cropTimer.current) clearTimeout(cropTimer.current);
    updateAssetCrop(tripId, assetId, next).catch(() => {});
  };
  // Delete the crop entirely → full frame.
  const removeCrop = () => {
    setCrop(null);
    setAspectId('source');
    saveCropNow(null);
  };
  // Discard any crop edits made since crop mode was opened, then exit.
  const cancelCrop = () => {
    const b = cropBaseline.current && cropBaseline.current.w > 0 ? cropBaseline.current : null;
    setCrop(b);
    saveCropNow(b);
    setCropMode(false);
  };
  // The live crop window the preview samples (the saved rect — which also carries
  // flip/straighten geometry — so the result preview matches the export).
  const cropGetter = () => crop;
  // Geometry (flip / straighten) lives on the crop object so it travels with it.
  const geo = { rotate: crop?.rotate || 0, flipH: !!crop?.flipH, flipV: !!crop?.flipV };
  const setGeo = (partial) => {
    const base = crop && Number(crop.w) > 0 ? crop : { x: 0, y: 0, w: 1, h: 1 };
    const next = { ...base, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV, ...partial };
    const identity = next.x === 0 && next.y === 0 && next.w >= 0.9999 && next.h >= 0.9999
      && !next.rotate && !next.flipH && !next.flipV;
    const v = identity ? null : next;
    setCrop(v);
    scheduleSaveCrop(v);
  };
  // While editing the crop box we show the FULL frame with geometry applied, so
  // the box (and its black straighten corners) read exactly like the export.
  const cropEditGetter = () => ({ x: 0, y: 0, w: 1, h: 1, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV });
  // Zoom = tighten/loosen the crop box around its center, preserving aspect.
  // 1× is the widest framing (fills a frame dimension); higher zooms in. Drag
  // the box afterwards to recompose. RED's oversampled RAW means moderate zoom
  // stays sharp at delivery res.
  const zoom = zoomFromScale(rectScale(crop));
  const applyZoom = (z) => {
    const rect = rectAtScale(crop, scaleFromZoom(z));
    const next = normalizeCrop(withGeo(rect));
    setCrop(next);
    scheduleSaveCrop(next);
  };

  // ---- local radial masks (Lightroom-style local adjustments) -------------
  const masks = Array.isArray(grade.masks) ? grade.masks : [];
  const addMask = () => {
    const id = `m${Date.now().toString(36)}`;
    const m = {
      id, type: 'radial', cx: 0.5, cy: 0.55, rx: 0.3, ry: 0.3,
      feather: 0.6, invert: false, amount: 1,
      adj: { ...DEFAULT_MASK_ADJ, exposure: 0.3 },
    };
    onChange({ ...grade, masks: [...masks, m] });
    setActiveMaskId(id);
    setMaskMode(true);
    setCropMode(false);
  };
  const addLinearMask = () => {
    const id = `m${Date.now().toString(36)}`;
    const m = {
      id, type: 'linear', cx: 0.5, cy: 0.5, rx: 0.3, ry: 0.3, angle: 90,
      feather: 0.4, invert: false, amount: 1,
      range: { ...DEFAULT_MASK_RANGE },
      adj: { ...DEFAULT_MASK_ADJ, exposure: -0.3 },
    };
    onChange({ ...grade, masks: [...masks, m] });
    setActiveMaskId(id);
    setMaskMode(true);
    setCropMode(false);
  };
  // Grab the current graded canvas as a downscaled JPEG (for AI subject detect).
  const capturePhotoFrame = () => {
    const scope = stageRef.current || document;
    const cv = scope.querySelector('.cs-cap-photo');
    if (!cv || !cv.width) return null;
    const s = Math.min(1, 1024 / cv.width);
    const oc = document.createElement('canvas');
    oc.width = Math.max(1, Math.round(cv.width * s));
    oc.height = Math.max(1, Math.round(cv.height * s));
    const ctx = oc.getContext('2d');
    if (!ctx) return null;
    try {
      ctx.drawImage(cv, 0, 0, oc.width, oc.height);
      return oc.toDataURL('image/jpeg', 0.85);
    } catch { return null; }
  };
  // Frames for the color assistant: the flat master (source) + the current
  // graded canvas, so Grok can judge the look against the original.
  const captureAssistantFrames = async () => {
    const frames = [];
    try {
      const w = source?.naturalWidth || source?.width;
      const h = source?.naturalHeight || source?.height;
      if (w && h) {
        const s = Math.min(1, 1024 / w);
        const oc = document.createElement('canvas');
        oc.width = Math.max(1, Math.round(w * s));
        oc.height = Math.max(1, Math.round(h * s));
        const ctx = oc.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0, oc.width, oc.height);
          frames.push({ label: 'original master', dataUrl: oc.toDataURL('image/jpeg', 0.85) });
        }
      }
    } catch { /* source not a drawable image (e.g. RAW float proxy) — skip */ }
    const cur = capturePhotoFrame();
    if (cur) frames.push({ label: 'current graded', dataUrl: cur });
    return frames;
  };

  // AI masks: "sky" is a top-down luminance-gated gradient (no model needed);
  // "subject" asks Grok Vision for the main subject's box and centers a radial.
  const addAiMask = async (target) => {
    if (busyAi) return;
    if (target === 'sky') {
      const id = `m${Date.now().toString(36)}`;
      onChange({
        ...grade,
        masks: [...masks, {
          id, type: 'linear', ai: 'sky', cx: 0.5, cy: 0.45, rx: 0.3, ry: 0.3, angle: -90,
          feather: 0.4, invert: false, amount: 1,
          range: { enabled: true, lo: 0.45, hi: 1, feather: 0.18 },
          adj: { ...DEFAULT_MASK_ADJ, exposure: -0.4, saturation: 8 },
        }],
      });
      setActiveMaskId(id);
      setMaskMode(true);
      setCropMode(false);
      return;
    }
    // subject
    setBusyAi('subject');
    try {
      const dataUrl = capturePhotoFrame();
      if (!dataUrl) throw new Error('no frame');
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const res = await fetch(`${API}/ai/region`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ target: 'subject', images: [{ label: 'frame', dataUrl }] }),
      });
      const data = res.ok ? await res.json() : {};
      const box = data && data.found
        ? { cx: data.cx, cy: data.cy, rx: Math.max(0.08, data.rx * 1.15), ry: Math.max(0.08, data.ry * 1.15) }
        : { cx: 0.5, cy: 0.55, rx: 0.3, ry: 0.3 };
      const id = `m${Date.now().toString(36)}`;
      onChange({
        ...grade,
        masks: [...masks, {
          id, type: 'radial', ai: 'subject', ...box,
          feather: 0.5, invert: false, amount: 1,
          range: { ...DEFAULT_MASK_RANGE },
          adj: { ...DEFAULT_MASK_ADJ, exposure: 0.25 },
        }],
      });
      setActiveMaskId(id);
      setMaskMode(true);
      setCropMode(false);
    } catch {
      /* leave a manual radial as fallback */
      addMask();
    } finally {
      setBusyAi(null);
    }
  };
  // ---- Voice copilot wiring ------------------------------------------------
  const assistantRef = useRef(null);
  // Load a target reference image, then jump straight into the match flow.
  const onPickReferenceFile = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      setReference({ dataUrl: String(r.result), name: file.name });
      assistantRef.current?.openMatch({ autoRun: true });
    };
    r.readAsDataURL(file);
  };
  const onMatchClick = () => {
    if (reference) assistantRef.current?.openMatch();
    else refInputRef.current?.click();
  };
  // Auto-align: structurally match our frame's crop to the reference's framing
  // (grade-invariant), so the ΔE comparison is fair without manual nudging.
  const autoAlignToReference = async () => {
    if (!reference?.dataUrl || !source || autoAligning) return false;
    setAutoAligning(true);
    try {
      const refEl = await loadImageEl(reference.dataUrl);
      const rect = autoAlignCrop(source, refEl);
      if (rect && rect.w > 0 && rect.h > 0) {
        onCropChange(rect);
        setAspectId('free');
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setAutoAligning(false);
    }
  };
  useAgentHandlers({
    apply_look: ({ name }) => {
      const p = resolveLook(name, presets || []);
      if (!p) return { ok: false, error: `no look matching "${name}"` };
      onChange(applyLookGrade(grade, p));
      return { ok: true, look: p.name };
    },
    adjust: (args) => {
      const { grade: next, applied } = adjustGrade(grade, args);
      if (!applied) return { ok: false, error: `unknown control "${args.control}"` };
      onChange(next);
      return { ok: true, ...applied };
    },
    reset_grade: () => { onChange(baseGrade(grade)); return { ok: true }; },
    add_mask: ({ target }) => { addAiMask(target === 'sky' ? 'sky' : 'subject'); return { ok: true, target: target || 'subject' }; },
    generate_variations: ({ direction } = {}) => {
      if (!assistantRef.current) return { ok: false, error: 'assistant not ready' };
      assistantRef.current.runVariations(direction);
      return { ok: true };
    },
  });
  useAgentContext(
    { pageName: 'Photo editor', movieName: asset?.name, presets: presetNames(presets || []) },
    [asset?.name, (presets || []).length],
  );

  const patchMask = (id, patch) =>
    onChange({ ...grade, masks: masks.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  const patchMaskAdj = (id, key, val) =>
    onChange({ ...grade, masks: masks.map((m) => (m.id === id ? { ...m, adj: { ...(m.adj || {}), [key]: val } } : m)) });
  const deleteMask = (id) => {
    onChange({ ...grade, masks: masks.filter((m) => m.id !== id) });
    if (activeMaskId === id) setActiveMaskId(null);
  };
  const toggleMaskMode = () => {
    setMaskMode((v) => !v);
    setCropMode(false);
    setHealMode(false);
    if (!activeMaskId && masks.length) setActiveMaskId(masks[0].id);
  };

  // ---- healing / spot removal (clone over blemishes, dust, distractions) ---
  // Spots live in SOURCE-frame space, applied before crop/geometry — mirrored by
  // GLSL healedSample() + ffmpeg healFilters() so the preview matches the bake.
  const healSpots = Array.isArray(grade.heal) ? grade.heal : [];
  const activeHeal = healSpots.find((s) => s.id === activeHealId);
  // Heal-mode preview shows the full, un-cropped frame so handles map to source uv.
  const healGetter = () => ({ x: 0, y: 0, w: 1, h: 1 });
  const addHealSpot = () => {
    if (healSpots.length >= 8) return; // shader supports up to 8 live spots
    const id = `h${Date.now().toString(36)}`;
    const s = { id, x: 0.5, y: 0.5, r: 0.05, feather: 0.5, ox: 0.14, oy: 0 };
    onChange({ ...grade, heal: [...healSpots, s] });
    setActiveHealId(id);
    setHealMode(true);
    setMaskMode(false);
    setCropMode(false);
  };
  const patchHeal = (id, patch) =>
    onChange({ ...grade, heal: healSpots.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const deleteHeal = (id) =>
    onChange({ ...grade, heal: healSpots.filter((s) => s.id !== id) });
  const toggleHealMode = () => {
    setHealMode((v) => !v);
    setMaskMode(false);
    setCropMode(false);
    if (!activeHealId && healSpots.length) setActiveHealId(healSpots[0].id);
  };

  // Copy / paste develop settings (Lightroom-style) — the whole creative grade
  // (incl. masks) travels via localStorage so you can sync a look onto another
  // photo. The crop/geometry stays per-photo.
  const copyLook = () => {
    try {
      localStorage.setItem('cs-look-clipboard', JSON.stringify(grade));
      setHasClip(true);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  const pasteLook = () => {
    try {
      const raw = localStorage.getItem('cs-look-clipboard');
      if (!raw) return;
      const g = JSON.parse(raw);
      if (g && typeof g === 'object') onChange(clone(g));
    } catch { /* ignore */ }
  };

  // Spawn a NEW, non-destructive variation of this frame from the current grade
  // (incl. masks + crop), baked as a sibling so it lines up in Compare. Lets the
  // editor branch a look without overwriting the version you opened.
  const [savingVer, setSavingVer] = useState(false);
  // Upscale picker for the "upscale → new version" action. Reframe + AI upscale
  // happen on the working negative BEFORE grading, so detail is recovered up front.
  const [upModel, setUpModel] = useState('real-esrgan');
  const [upScale, setUpScale] = useState(2);
  // Bake the current grade (+ crop, + optional upscale) as a NEW sibling version.
  const makeVersion = async ({ upscale = null, suffix = 'variant', extraTags = [] } = {}) => {
    if (savingVer || !asset || !canReviseAsset(asset)) return;
    setSavingVer(true);
    try {
      const newId = await createStillGrabAsset(tripId, {
        source: {
          id: asset.lineage?.sourceAssetId || asset.stillGrab?.sourceAssetId || '',
          name: asset.lineage?.sourceName || asset.name || 'clip',
        },
        timeSec: asset.stillGrab?.timeSec ?? asset.lineage?.sourceTimeSec ?? 0,
        frameIndex: Number.isFinite(Number(asset.stillGrab?.frameIndex)) ? Number(asset.stillGrab.frameIndex) : null,
        grade: clone(grade),
        applyGrade: true,
        crop: crop || asset.crop || null,
        redSource: !!(asset.lineage?.sourceIsRaw || asset.isRaw),
        name: `${String(asset.name || 'frame').replace(/\.[^.]+$/, '')} · ${suffix}`,
        tags: ['nl-revise', ...extraTags],
        revisedFromAssetId: asset.id || '',
        upscale,
      });
      navigate(`/photo/${tripId}/${newId}`);
    } catch { /* surfaced via the disabled state resetting */ } finally {
      setSavingVer(false);
    }
  };
  const saveAsNewVersion = () => makeVersion();
  const upscaleToNewVersion = () => makeVersion({
    upscale: { enabled: true, model: upModel, scale: upScale },
    suffix: `${upScale}× ${upModel}`,
    extraTags: ['upscale'],
  });

  // Import a creative .cube LUT — parsed + resampled to 33³ client-side and
  // stored compactly on the grade; applied as a second LUT stage (preview==bake).
  const importLut = async (file) => {
    setLutErr('');
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { setLutErr('LUT file too large (max 30 MB).'); return; }
    try {
      const text = await file.text();
      const lut = lutFromCubeText(text, file.name.replace(/\.cube$/i, ''));
      if (!lut) { setLutErr('Could not parse that .cube (3D LUT expected).'); return; }
      onChange({ ...grade, lut });
    } catch {
      setLutErr('Could not read that file.');
    }
  };
  const removeLut = () => { const g = { ...grade }; delete g.lut; onChange(g); };

  // Surface the look applied automatically at grab time (e.g. RED IPP2 Natural)
  // so it's clear the "creative" grade has a known starting point on top of the
  // neutral base — and let the user strip it back to that neutral base.
  const neutralBase = baseGrade(grade);
  const isNeutral = gradeSignature(grade) === gradeSignature(neutralBase);
  const startLookName =
    asset?.startingLook?.name ||
    (grade.inputTransform && grade.inputTransform !== 'srgb' ? 'RED IPP2 Natural' : null);
  const removeStartingLook = () => onChange(baseGrade(grade));

  if (asset === undefined) return <div className="cs-page">Loading…</div>;
  if (asset === null) return <div className="cs-page">Photo not found.</div>;

  const needsDevelop = develop.status !== 'ready' || !proxyUrl;
  const developing = develop.status === 'queued' || develop.status === 'developing';
  const baking = gradeRender.status === 'queued' || gradeRender.status === 'rendering';
  const dirty = gradeSignature(grade) !== gradeSignature(asset.grade || DEFAULT_GRADE)
    || (grade.lut?.id || '') !== (asset.grade?.lut?.id || '');
  const selected = EXPORT_OPTIONS.filter((o) => fmts[o.id]).map((o) => o.id);
  const derivatives = asset.derivatives || {};
  const stageDerivatives = asset.stageDerivatives || {};
  const runExport = () => {
    requestPhotoGrade(tripId, assetId, grade, selected.length ? selected : ['magazine']);
    setExportOpen(false);
  };
  // Fast, low-res "dial the look" bake: re-bakes the REAL pipeline (crop + grade +
  // masks) from the master negative at a small long edge, skipping AI upscale and
  // fulljpeg. Sub-second even for masked grades, so you can iterate the look
  // before paying for a full-res / upscaled bake. The card shows a DRAFT badge
  // until a real Export / Upscale restores full quality.
  const isDraftPreview = asset.previewQuality === 'draft';
  const runDraft = () => requestPhotoGrade(tripId, assetId, grade, ['magazine'], [], { draft: true });
  // Single-pane preview shows the working grade directly (stage scrubbing now
  // lives in the guided workflow, not a separate viewStage concept).
  const viewGrade = grade;
  // Before/After: left = as-shot negative, right = the working grade.
  const stageBase = DEFAULT_GRADE;
  const beforeLabel = 'Negative (as shot)';
  // Stage-by-stage workflow: left pane = cumulative grade through the PREVIOUS
  // stage, right pane = that plus the current stage's edits, so each step shows
  // exactly what this stage adds on top of everything dialed so far.
  const stageDef = STAGES[stageIdx] || STAGES[0];
  const stagePhase = phaseOfStage(stageDef.id);
  // The AI auto-grade reference, run through the SAME color-tame clamp the backend
  // now applies at bake time — so "View AI auto-grade" shows the photographic
  // result that actually bakes, not the old over-saturated "spilled paint" look.
  const aiRef = aiGrade ? tameProofColor(aiGrade) : grade;
  // Left/before pane: either the cumulative result through the PREVIOUS step
  // (default — see what THIS step adds), or the AI auto-grade (compare your build
  // against the model's result you're trying to match/beat).
  const stageBefore = stageTarget === 'ai'
    ? aiRef
    : stageIdx === 0 ? DEFAULT_GRADE : gradeUpToStage(grade, stageIdx - 1);
  const stageAfter = gradeUpToStage(grade, stageIdx);
  const stageBeforeLabel = stageTarget === 'ai'
    ? 'AI auto-grade'
    : stageIdx === 0 ? 'Negative (as shot)' : `Through ${STAGES[stageIdx - 1].name}`;
  const stageAfterLabel = stageIdx === 0 ? 'Decoded' : `+ ${stageDef.name}`;
  // The auto model's per-adjustment recommendation for this frame, derived from
  // the stage INPUT histogram (left pane), so the coach explains what to do & why.
  const stageSug = compare === 'stage'
    ? computeStageSuggestions(beforeStats, { inputTransform: grade?.inputTransform, kind: 'photo' })
    : null;
  // Re-key the stage suggestions by dotted grade path so GradePanel can render an
  // inline "Auto: X · Apply" chip directly under each matching control in the dock.
  const stageSugByPath = stageSuggestionsByPath(stageDef, stageSug);
  // The local stage's "auto" isn't slider suggestions — it's the AI's perception
  // masks (water teal + subject lift). Pull the tamed versions out of aiGrade.
  const aiAutoMasks = aiGrade
    ? (tameProofColor(aiGrade).masks || []).filter((m) => m && typeof m.source === 'string' && m.source.startsWith('auto-'))
    : [];
  const localCanApply = stageDef.id === 'local' && aiAutoMasks.length > 0;
  const applyStageSuggestions = () => {
    if (stageDef.id === 'local') {
      if (!aiAutoMasks.length) return;
      const userMasks = (grade.masks || []).filter((m) => !(m && typeof m.source === 'string' && m.source.startsWith('auto-')));
      onChange({ ...grade, masks: [...userMasks, ...clone(aiAutoMasks)] });
      return;
    }
    // Guidance/taste entries (skipAuto) stay chip-only; this writes only the
    // measured suggestions for the stage.
    const next = applyPathSuggestions(grade, stageSugByPath);
    if (next) onChange(next);
  };
  const startFromNegative = () => { onChange(baseGrade(grade)); setStageIdx(0); };
  const restoreAiGrade = () => { if (aiGrade) onChange(tameProofColor(aiGrade)); };
  const goToStageId = (id) => {
    const i = STAGES.findIndex((s) => s.id === id);
    if (i >= 0) { setStageIdx(i); setCompare('stage'); }
  };
  // Ask the AI a teaching question about the current stage. Returns prose advice
  // (not grade ops): it gets the stage + tools, the frame's active settings, and
  // the current graded frame so it can answer "what/why/which" for this shot.
  const askAi = async (qOverride) => {
    const question = (typeof qOverride === 'string' ? qOverride : askInput).trim();
    if (!question || askBusy) return;
    if (!API) { setAskErr('AI assistant isn’t configured in this build.'); return; }
    setAskErr('');
    setAskInput('');
    setAskOpen(true);
    const priorHistory = askThread.map((m) => ({ role: m.role, text: m.text }));
    setAskThread((t) => [...t, { role: 'user', text: question }]);
    setAskBusy(true);
    try {
      let images = [];
      try {
        const frames = await captureAssistantFrames();
        const cur = frames.find((f) => /current graded/i.test(f.label)) || frames[frames.length - 1];
        if (cur) images = [{ label: 'current graded frame', dataUrl: cur.dataUrl }];
      } catch { /* frame is optional context */ }
      const settings = activeAdjustments(grade).map((a) => ({ key: a.key, label: a.label, value: a.value, unit: a.unit }));
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const res = await fetch(`${API}/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          question,
          stage: {
            id: stageDef.id,
            name: stageDef.name,
            goal: stageDef.goal,
            tools: stageDef.tools.map((t) => ({ label: t.label, what: t.what })),
          },
          settings,
          images,
          history: priorHistory,
        }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
        throw new Error(detail || `request failed (${res.status})`);
      }
      const data = await res.json();
      setAskThread((t) => [...t, { role: 'assistant', text: data.answer || 'No answer returned.' }]);
    } catch (e) {
      setAskErr(e.message || 'Ask failed');
      setAskThread((t) => [...t, { role: 'assistant', text: `⚠️ ${e.message || 'Ask failed'}`, error: true }]);
    } finally {
      setAskBusy(false);
    }
  };
  const runStageExport = (stages) => {
    requestPhotoGrade(tripId, assetId, grade, [], stages);
  };
  // Jump back to the source clip to grab a different frame or crop straight from
  // the original (full RAW for RED). The current frame time is pre-seeked.
  const canRegrab = lineage.origin === 'frame-grab' && !!lineage.sourceAssetId;
  const regrab = () => {
    if (!canRegrab) return;
    navigate(`/watch/asset/${tripId}/${lineage.sourceAssetId}`, {
      state: { grab: true, grabTime: Number(lineage.sourceTimeSec) || 0 },
    });
  };

  return (
    <div className="cs-page cs-edit-page cs-edit-photo">
      <div className="cs-editbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(-1)}>←</button>
          <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {asset.isRaw ? 'RAW · ' : ''}{asset.name}
          </h1>
        </div>
        <div className="cs-studio-toolrow" style={{ position: 'relative' }}>
          {canRegrab && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={regrab}
              title="Pick a different frame or crop straight from the original clip (full RAW for RED)"
            >
              ↺ Re-grab from clip
            </button>
          )}
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${cropMode ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => {
                const turningOn = !cropMode;
                if (turningOn) cropBaseline.current = crop; // snapshot for Cancel
                setCropMode(turningOn);
                setMaskMode(false);
                setHealMode(false);
              }}
              title="Crop & reframe — non-destructive, applied to exports"
            >
              {cropMode ? '✂ Done cropping' : `✂ Crop${crop ? ' · on' : ''}`}
            </button>
          )}
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${maskMode ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={toggleMaskMode}
              title="Local masks — radial, gradient, AI sky/subject. Non-destructive, exported faithfully."
            >
              {maskMode ? '◎ Done masking' : `◎ Masks${masks.length ? ` · ${masks.length}` : ''}`}
            </button>
          )}
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${healMode ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={toggleHealMode}
              title="Healing / spot removal — clone over blemishes, dust, distractions. Non-destructive, exported faithfully."
            >
              {healMode ? '✚ Done healing' : `✚ Heal${healSpots.length ? ` · ${healSpots.length}` : ''}`}
            </button>
          )}
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${reference ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={onMatchClick}
              title="Match a reference photo — load the look you want to reproduce and let the AI converge the grade onto it (ΔE-guided)."
            >
              {reference ? '🎯 Matching…' : '🎯 Match a photo'}
            </button>
          )}
          <input
            ref={refInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { onPickReferenceFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          {!needsDevelop && (
            <button
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={copyLook}
              title="Copy this photo’s develop settings (incl. masks) to paste onto another photo"
            >
              {copied ? '✓ Copied' : '⧉ Copy look'}
            </button>
          )}
          {!needsDevelop && hasClip && (
            <button
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={pasteLook}
              title="Apply the copied develop settings to this photo"
            >
              ⧉ Paste look
            </button>
          )}
          {!needsDevelop && canReviseAsset(asset) && (
            <button
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={saveAsNewVersion}
              disabled={savingVer}
              title="Bake the current grade as a NEW version of this frame (keeps the original; lines up in Compare)"
            >
              {savingVer ? '⏳ Saving…' : '＋ Save as new version'}
            </button>
          )}
          {!needsDevelop && canReviseAsset(asset) && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Reframe + AI upscale run BEFORE the grade, on a working negative, so detail is recovered up front"
            >
              <select
                className="cs-sm-select"
                value={upModel}
                onChange={(e) => setUpModel(e.target.value)}
                disabled={savingVer}
              >
                <option value="real-esrgan">Real-ESRGAN · big frames</option>
                <option value="ultimate-sd">Ultimate SD · max detail</option>
                <option value="swinir">SwinIR · small crops</option>
              </select>
              <select
                className="cs-sm-select"
                value={upScale}
                onChange={(e) => setUpScale(Number(e.target.value))}
                disabled={savingVer}
              >
                <option value={2}>2×</option>
                <option value={4}>4×</option>
              </select>
              <button
                className="cs-btn cs-btn-sm cs-btn-ghost"
                onClick={upscaleToNewVersion}
                disabled={savingVer}
                title="Bake a new version with the current grade + crop and AI-upscale it (Replicate)"
              >
                {savingVer ? '⏳ Saving…' : '⤴ Upscale → new version'}
              </button>
            </span>
          )}
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${grade.lut ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => (grade.lut ? removeLut() : lutInputRef.current?.click())}
              title={grade.lut ? `Creative LUT: ${grade.lut.name} — click to remove` : 'Import a creative .cube LUT (applied on top of the grade)'}
            >
              {grade.lut ? `LUT · ${grade.lut.name} ✕` : '◩ Import LUT'}
            </button>
          )}
          <input
            ref={lutInputRef}
            type="file"
            accept=".cube"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importLut(f); }}
          />
          {!needsDevelop && (
            <button
              className={`cs-btn cs-btn-sm ${isDraftPreview ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              disabled={baking}
              onClick={runDraft}
              title="Fast low-res bake (no AI upscale) to check the REAL crop + grade + masks before paying for a full-res bake"
            >
              {baking ? '⏳ Baking…' : '⚡ Draft preview'}
            </button>
          )}
          <button
            className="cs-btn cs-btn-primary cs-btn-sm"
            disabled={needsDevelop || baking}
            onClick={() => setExportOpen((v) => !v)}
            title="Bake the current color & light into delivery files"
          >
            {baking ? 'Exporting…' : 'Export ▾'}
          </button>
          {exportOpen && !needsDevelop && (
            <div className="cs-export-pop">
              <div className="cs-export-title">Export formats</div>
              {EXPORT_OPTIONS.map((o) => (
                <label key={o.id} className="cs-export-opt">
                  <input
                    type="checkbox"
                    checked={!!fmts[o.id]}
                    onChange={(e) => setFmts((s) => ({ ...s, [o.id]: e.target.checked }))}
                  />
                  <span className="cs-export-opt-l">{o.label}</span>
                  <span className="cs-export-opt-h">{o.hint}</span>
                </label>
              ))}
              <button
                className="cs-btn cs-btn-primary cs-btn-sm"
                disabled={baking || !selected.length}
                onClick={runExport}
                style={{ marginTop: 8, width: '100%' }}
              >
                {`Export ${selected.length} format${selected.length === 1 ? '' : 's'}`}
              </button>

              <div className="cs-export-title" style={{ marginTop: 12 }}>RAW-like handoff · 16-bit TIFF</div>
              <div className="cs-help" style={{ fontSize: 11, margin: '2px 0 6px', lineHeight: 1.4 }}>
                Maximum latitude for Lightroom / Resolve — your crop applied, the creative look
                <b> not</b> baked. Links appear in the Pro color workflow panel when ready.
              </div>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                style={{ width: '100%', marginBottom: 6 }}
                disabled={baking}
                onClick={() => { runStageExport([{ id: 'negative', formats: ['tiff16'] }]); setExportOpen(false); }}
                title="The flat log / Wide-Gamut negative — the most RAW-like file (de-log it yourself in Lightroom)"
              >
                Flat log negative
              </button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                style={{ width: '100%' }}
                disabled={baking}
                onClick={() => { runStageExport([{ id: 'neutral', formats: ['tiff16'] }]); setExportOpen(false); }}
                title="De-logged to a clean Rec.709 base (display transform only, no creative look) — the neutral file to hand to Lightroom"
              >
                Neutral base (Rec.709)
              </button>
            </div>
          )}
        </div>
      </div>

      {needsDevelop ? (
        <div className="cs-section">
          {(developing || (!asset.isRaw && develop.status !== 'error')) ? (
            <>
              <div className="cs-help">{asset.isRaw ? 'Developing RAW' : 'Preparing for editing'} — building a 16-bit proxy…</div>
              <div className="cs-progress" style={{ marginTop: 8 }}><div style={{ width: '40%' }} /></div>
            </>
          ) : (
            <div>
              <div className="cs-empty" style={{ marginBottom: 10 }}>
                {develop.status === 'error'
                  ? `Develop failed: ${develop.message || 'unknown error'}`
                  : asset.isRaw
                    ? 'This RAW needs to be developed before editing (16-bit highlight/shadow latitude).'
                    : 'Prepare this photo for full-fidelity editing.'}
              </div>
              <button className="cs-btn cs-btn-primary" onClick={() => requestDevelop(tripId, assetId)}>
                {asset.isRaw ? 'Develop RAW' : 'Prepare for editing'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="cs-studio cs-studio-photo">
          <div className={`cs-studio-main${maxPreview ? ' cs-studio-main--maxpreview' : ''}`}>
            <div className="cs-studio-stage" ref={stageRef}>
              {maxPreview && (
                <button
                  type="button"
                  className="cs-btn cs-btn-sm cs-stage-exitmax"
                  onClick={() => setMaxPreview(false)}
                  title="Exit full preview (Esc)"
                >
                  ✕ Exit full preview
                </button>
              )}
              {loadErr ? (
                <div className="cs-empty">Could not load proxy: {loadErr}</div>
              ) : !source ? (
                <div className="cs-help" style={{ padding: 40 }}>Loading image…</div>
              ) : cropMode ? (
                <>
                  <div className="cs-studio-cropwrap">
                    <GradedCanvas
                      source={source}
                      grade={viewGrade}
                      getCrop={cropEditGetter}
                      onError={setLoadErr}
                      className="cs-studio-canvas"
                    />
                    <CropBox rect={crop || DEFAULT_CROP_RECT} lockRatio={lockRatio} onChange={onCropChange} />
                    {reference && refOpacity > 0 && (
                      <img
                        src={reference.dataUrl}
                        alt="reference align overlay"
                        className="cs-ref-overlay"
                        style={{ opacity: refOpacity, mixBlendMode: refDiff ? 'difference' : 'normal' }}
                      />
                    )}
                  </div>
                  <div className="cs-crop-toolbar">
                    {reference && (
                      <>
                        <span className="cs-crop-grp-label">Align ref</span>
                        <button
                          className="cs-btn cs-btn-sm cs-btn-primary"
                          onClick={autoAlignToReference}
                          disabled={autoAligning}
                          title="Auto-align — structurally match this frame's crop to the reference framing (grade-invariant)"
                        >
                          {autoAligning ? <><span className="cs-spinner" /> Aligning…</> : '✨ Auto-align'}
                        </button>
                        <input
                          type="range" min="0" max="1" step="0.02" value={refOpacity}
                          onChange={(e) => setRefOpacity(Number(e.target.value))}
                          title="Reference overlay opacity — check the auto-align, or nudge the framing manually"
                          style={{ width: 90 }}
                        />
                        <button
                          className={`cs-btn cs-btn-sm ${refDiff ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                          onClick={() => setRefDiff((v) => !v)}
                          title="Difference blend — darkest = best aligned"
                        >Diff</button>
                        <span className="cs-crop-sep" />
                      </>
                    )}
                    <span className="cs-crop-grp-label">Zoom</span>
                    <input
                      type="range"
                      min={1}
                      max={4}
                      step={0.02}
                      value={zoom}
                      onChange={(e) => applyZoom(Number(e.target.value))}
                      title="Zoom / tighten the framing (post-crop). RED's high-res RAW keeps moderate zoom sharp; drag the box to recompose."
                      style={{ width: 120 }}
                    />
                    <span className="cs-crop-grp-label" style={{ minWidth: 38 }}>{zoom.toFixed(2)}×</span>
                    {zoom > 1.005 && (
                      <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => applyZoom(1)} title="Reset zoom to the widest framing">⟲</button>
                    )}
                    <span className="cs-crop-sep" />
                    <span className="cs-crop-grp-label">Straighten</span>
                    <input
                      type="range"
                      min={-45}
                      max={45}
                      step={0.1}
                      value={geo.rotate}
                      onChange={(e) => setGeo({ rotate: Number(e.target.value) })}
                      title="Rotate to level the horizon (−45°…45°)"
                      style={{ width: 120 }}
                    />
                    <span className="cs-crop-grp-label" style={{ minWidth: 38 }}>{geo.rotate ? `${geo.rotate.toFixed(1)}°` : '0°'}</span>
                    {geo.rotate !== 0 && (
                      <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setGeo({ rotate: 0 })} title="Reset straighten">⟲</button>
                    )}
                    <span className="cs-crop-sep" />
                    <button
                      className={`cs-btn cs-btn-sm ${geo.flipH ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      onClick={() => setGeo({ flipH: !geo.flipH })}
                      title="Flip horizontal"
                    >⇋ Flip H</button>
                    <button
                      className={`cs-btn cs-btn-sm ${geo.flipV ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      onClick={() => setGeo({ flipV: !geo.flipV })}
                      title="Flip vertical"
                    >⇅ Flip V</button>
                    <span className="cs-crop-sep" />
                    <span className="cs-crop-grp-label">Lock ratio</span>
                    {CROP_ASPECTS.map((a) => {
                      const on = aspectId === a.id && !!crop;
                      return (
                        <button
                          key={a.id}
                          className={`cs-btn cs-btn-sm ${on ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                          onClick={() => chooseAspect(a.id)}
                          title={
                            a.id === 'source'
                              ? 'Lock the box to the original frame’s aspect ratio'
                              : a.id === 'free'
                                ? 'Any shape — drag corners/edges'
                                : `Lock to ${a.label}`
                          }
                        >
                          {a.label}
                        </button>
                      );
                    })}
                    <span className="cs-crop-sep" />
                    <button
                      className="cs-btn cs-btn-sm cs-btn-ghost cs-crop-remove"
                      disabled={!crop}
                      onClick={removeCrop}
                      title="Delete the crop — use the whole frame"
                    >
                      🗑 Remove crop
                    </button>
                    <button
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      onClick={cancelCrop}
                      title="Discard crop changes made since you opened crop"
                    >
                      Cancel
                    </button>
                    <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setCropMode(false)}>Done</button>
                  </div>
                </>
              ) : maskMode ? (
                <>
                  <div className="cs-studio-cropwrap">
                    <GradedCanvas
                      source={source}
                      grade={viewGrade}
                      getCrop={cropGetter}
                      onStats={setStats}
                      onError={setLoadErr}
                      className="cs-studio-canvas cs-cap-photo"
                    />
                    {masks.map((m) => (
                      <MaskBox
                        key={m.id}
                        mask={m}
                        selected={m.id === activeMaskId}
                        onSelect={() => setActiveMaskId(m.id)}
                        onChange={(geom) => patchMask(m.id, geom)}
                      />
                    ))}
                  </div>
                  <div className="cs-crop-toolbar">
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={addMask}>+ Radial</button>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={addLinearMask}>+ Gradient</button>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => addAiMask('sky')} disabled={!!busyAi}>☁ Sky</button>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => addAiMask('subject')} disabled={!!busyAi}>
                      {busyAi === 'subject' ? '…' : '◉ Subject'}
                    </button>
                    {activeMaskId && (
                      <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => deleteMask(activeMaskId)}>Delete</button>
                    )}
                    <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setMaskMode(false)}>Done</button>
                  </div>
                </>
              ) : healMode ? (
                <>
                  <div className="cs-studio-cropwrap">
                    <GradedCanvas
                      source={source}
                      grade={viewGrade}
                      getCrop={healGetter}
                      onStats={setStats}
                      onError={setLoadErr}
                      className="cs-studio-canvas cs-cap-photo"
                    />
                    {healSpots.map((s) => (
                      <HealBox
                        key={s.id}
                        spot={s}
                        selected={s.id === activeHealId}
                        onSelect={() => setActiveHealId(s.id)}
                        onChange={(patch) => patchHeal(s.id, patch)}
                      />
                    ))}
                  </div>
                  <div className="cs-crop-toolbar">
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={addHealSpot} disabled={healSpots.length >= 8}>+ Spot</button>
                    {activeHeal && (
                      <>
                        <label className="cs-heal-ctl">Size
                          <input
                            type="range" min="0.01" max="0.4" step="0.005" value={activeHeal.r}
                            onChange={(e) => patchHeal(activeHeal.id, { r: Number(e.target.value) })}
                          />
                        </label>
                        <label className="cs-heal-ctl">Feather
                          <input
                            type="range" min="0" max="0.95" step="0.05" value={activeHeal.feather == null ? 0.5 : activeHeal.feather}
                            onChange={(e) => patchHeal(activeHeal.id, { feather: Number(e.target.value) })}
                          />
                        </label>
                        <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => deleteHeal(activeHeal.id)}>Delete</button>
                      </>
                    )}
                    <span className="cs-help">Drag the red circle onto a blemish, the green one to clean texture to clone from.</span>
                    <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setHealMode(false)}>Done</button>
                  </div>
                </>
              ) : showAi ? (
                <>
                  <GradedCanvas
                    source={source}
                    grade={aiRef}
                    getCrop={cropGetter}
                    onStats={setStats}
                    onError={setLoadErr}
                    className="cs-studio-canvas cs-cap-photo"
                  />
                  <button className="cs-stage-badge" onClick={() => setShowAi(false)} title="Back to your workflow">
                    Viewing: AI auto-grade · ✕
                  </button>
                </>
              ) : compare === 'stage' ? (
                <SplitPreview
                  panes={[
                    { label: stageBeforeLabel, color: '#888' },
                    { label: stageAfterLabel, color: 'var(--surf)' },
                  ]}
                >
                  <GradedCanvas source={source} grade={stageBefore} getCrop={cropGetter} onStats={setBeforeStats} onError={setLoadErr} className="cs-cap-stage-before" />
                  <GradedCanvas source={source} grade={stageAfter} getCrop={cropGetter} onStats={setStats} onError={setLoadErr} className="cs-cap-stage-after" />
                </SplitPreview>
              ) : compare === 'off' ? (
                <>
                  <GradedCanvas
                    source={source}
                    grade={viewGrade}
                    getCrop={cropGetter}
                    onStats={setStats}
                    onError={setLoadErr}
                    className="cs-studio-canvas cs-cap-photo"
                  />
                </>
              ) : compare === 'ba' ? (
                <SplitPreview panes={[{ label: beforeLabel, color: '#888' }, { label: 'Your grade', color: 'var(--surf)' }]}>
                  <GradedCanvas source={source} grade={stageBase} getCrop={cropGetter} onError={setLoadErr} />
                  <GradedCanvas source={source} grade={grade} getCrop={cropGetter} onStats={setStats} onError={setLoadErr} />
                </SplitPreview>
              ) : (
                <SplitPreview
                  panes={[
                    { label: '+ Light', color: 'var(--surf)' },
                    { label: '+ Color', color: 'var(--accent)' },
                    { label: 'Final', color: '#ddd' },
                  ]}
                >
                  <GradedCanvas source={source} grade={gradeLayer(grade, 'light')} getCrop={cropGetter} onError={setLoadErr} />
                  <GradedCanvas source={source} grade={gradeLayer(grade, 'color')} getCrop={cropGetter} onError={setLoadErr} />
                  <GradedCanvas source={source} grade={grade} getCrop={cropGetter} onStats={setStats} onError={setLoadErr} />
                </SplitPreview>
              )}
            </div>
            <div className="cs-studio-bar">
              <div className="cs-segment cs-segment-sm">
                <button className={compare === 'stage' && !showAi ? 'on' : ''} onClick={() => { setShowAi(false); setCompare('stage'); }}>Stages</button>
                <button className={compare === 'off' && !showAi ? 'on' : ''} onClick={() => { setShowAi(false); setCompare('off'); }}>Single</button>
                <button className={compare === 'ba' && !showAi ? 'on' : ''} onClick={() => { setShowAi(false); setCompare('ba'); }}>Before / After</button>
                <button className={compare === 'stack' && !showAi ? 'on' : ''} onClick={() => { setShowAi(false); setCompare('stack'); }}>Layer stack</button>
              </div>
              <div className="cs-segment cs-segment-sm">
                <button onClick={undo} disabled={!canUndo} title="Undo the last change (⌘/Ctrl+Z)">↩ Undo</button>
                <button onClick={redo} disabled={!canRedo} title="Redo (⌘/Ctrl+Shift+Z)">Redo ↪</button>
              </div>
              <button
                className="cs-btn cs-btn-sm cs-btn-ghost"
                onClick={() => setMaxPreview(true)}
                title="Float the preview fullscreen on top — stage controls can't squash it (Esc to exit)"
              >
                ⤢ Maximize preview
              </button>
              <button
                className="cs-btn cs-btn-sm cs-btn-ghost"
                onClick={() => setCfgOpen(true)}
                title="Edit the live auto-grade tuning (subject lift, water teal, caps) — saved to Firestore, no deploy"
              >
                ⚙ Auto-grade defaults
              </button>
              <button
                className={`cs-btn cs-btn-sm ${showAi ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setShowAi((v) => !v)}
                disabled={!aiGrade}
                title="Preview the model's full baked auto-grade for this frame"
              >
                {showAi ? '✓ AI auto-grade' : 'View AI auto-grade'}
              </button>
              <span className="cs-help">
                {asset.isRaw ? '16-bit RAW develop' : '16-bit proxy'} · live WebGL preview matches the exported bake
                {dirty ? ' · unsaved grade (auto-saves)' : ''}
                {crop ? ' · cropped (re-crop any time — applied to exports)' : ''}
                {grade.lut ? ` · LUT: ${grade.lut.name}` : ''}
              </span>
              {lutErr && <span className="cs-help" style={{ color: 'var(--accent)' }}>{lutErr}</span>}
              {isDraftPreview && gradeRender.status === 'ready' && (
                <span className="cs-help" style={{ color: 'var(--accent)' }}>
                  ⚡ Showing a low-res DRAFT — Export (or Upscale → new version) for full quality
                </span>
              )}
              {gradeRender.status === 'ready' &&
                (Object.keys(derivatives).length ? (
                  <span className="cs-export-dls">
                    <span className="cs-help">Download:</span>
                    {EXPORT_OPTIONS.filter((o) => derivatives[o.id]).map((o) => (
                      <a
                        key={o.id}
                        className="cs-btn cs-btn-ghost cs-btn-sm"
                        href={derivatives[o.id]}
                        download
                        target="_blank"
                        rel="noreferrer"
                      >
                        {o.label}
                      </a>
                    ))}
                  </span>
                ) : (
                  asset.derivedUrl && (
                    <a className="cs-btn cs-btn-ghost cs-btn-sm" href={asset.derivedUrl} download target="_blank" rel="noreferrer">
                      Download last export
                    </a>
                  )
                ))}
              {gradeRender.status === 'error' && (
                <span className="cs-help" style={{ color: 'var(--accent)' }}>Export failed: {gradeRender.message}</span>
              )}
            </div>
            {compare === 'stage' && !showAi && !maskMode && !healMode && (
              <div className="cs-workbench">
                <div className="cs-workbench-head">
                  <div className="cs-workbench-title">
                    <span className="cs-workbench-step">Step {stageIdx + 1}/{STAGES.length}</span>
                    <span className="cs-workbench-name">{stageDef.name}</span>
                    <span className="cs-workbench-phase">{stagePhase.name}</span>
                  </div>
                  <div className="cs-workbench-nav">
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      disabled={!canUndo}
                      onClick={undo}
                      title="Undo the last change (⌘/Ctrl+Z)"
                    >
                      ↩ Undo
                    </button>
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      disabled={!canRedo}
                      onClick={redo}
                      title="Redo (⌘/Ctrl+Shift+Z)"
                    >
                      Redo ↪
                    </button>
                    {(hasAutoSuggestions(stageSugByPath) || localCanApply) && (
                      <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={applyStageSuggestions}>
                        {stageDef.id === 'local' ? 'Pull in AI masks' : 'Apply all auto'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      disabled={stageIdx === 0}
                      onClick={() => setStageIdx((i) => Math.max(0, i - 1))}
                    >
                      ← Prev
                    </button>
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-primary"
                      disabled={stageIdx === STAGES.length - 1}
                      onClick={() => setStageIdx((i) => Math.min(STAGES.length - 1, i + 1))}
                    >
                      Next →
                    </button>
                  </div>
                </div>
                <div className="cs-workbench-goal"><b>Objective.</b> {stageDef.goal}</div>
                {/* Grok stage assist: the model sees the stage's actual input +
                    current-output panes and suggests values for just this
                    stage's controls — alongside the histogram auto chips. */}
                <StageAssist
                  stageDef={stageDef}
                  stages={STAGES}
                  grade={grade}
                  onChange={onChange}
                  capture={() => ({
                    before: captureCanvasFrame('.cs-cap-stage-before'),
                    after: captureCanvasFrame('.cs-cap-stage-after'),
                    // The match target (if one is loaded) rides along as the
                    // reference look — Grok reproduces its slice per stage.
                    reference: reference?.dataUrl || null,
                  })}
                  measurements={measurementLines(stageSugByPath)}
                  context={{ isRed: !!asset.isRaw, kind: 'photo', inputTransform: grade?.inputTransform }}
                  disabled={!source}
                />
                <div className="cs-workbench-grid">
                  <div className="cs-workbench-controls">
                    <GradePanel
                      grade={grade}
                      onChange={onChange}
                      kind="photo"
                      sections={stageDef.sections}
                      variant="dock"
                      suggestions={stageSugByPath}
                      presets={presets || []}
                      onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'photo' })}
                      onDeletePreset={(id) => deleteGradePreset(id)}
                    />
                  </div>
                  <div className="cs-workbench-coach">
                    <div className="cs-coach-tools">
                      {stageDef.tools.map((t) => (
                        <div key={t.label} className="cs-coach-tool">
                          <div className="cs-coach-tool-name">{t.label}</div>
                          <div className="cs-coach-tool-what">{t.what}</div>
                        </div>
                      ))}
                    </div>
                    <div className="cs-ask">
                      <div className="cs-ask-head">
                        <span className="cs-ask-badge">✨ Ask the AI</span>
                        <span className="cs-ask-sub">Stuck on a choice in this stage? Get a researched answer.</span>
                      </div>
                      {(STAGE_QUESTIONS[stageDef.id] || []).length > 0 && (
                        <div className="cs-ask-chips">
                          {(STAGE_QUESTIONS[stageDef.id] || []).map((q) => (
                            <button
                              key={q}
                              type="button"
                              className="cs-ask-chip"
                              onClick={() => askAi(q)}
                              disabled={askBusy}
                              title={q}
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      )}
                      {(askOpen && (askThread.length > 0 || askBusy)) && (
                        <div className="cs-ask-thread">
                          {askThread.map((m, i) => (
                            <div key={i} className={`cs-ask-msg cs-ask-${m.role}${m.error ? ' is-err' : ''}`}>
                              {m.role === 'user'
                                ? <div className="cs-ask-q">{m.text}</div>
                                : <div className="cs-ask-a"><AnswerText text={m.text} /></div>}
                            </div>
                          ))}
                          {askBusy && (
                            <div className="cs-ask-msg cs-ask-assistant">
                              <div className="cs-ask-a"><span className="cs-spinner" /> Researching…</div>
                            </div>
                          )}
                        </div>
                      )}
                      {askErr && <div className="cs-aiasst-err">{askErr}</div>}
                      <div className="cs-ask-input">
                        <textarea
                          rows={2}
                          value={askInput}
                          placeholder={`Ask about ${stageDef.name.toLowerCase()}… (Enter to send)`}
                          onChange={(e) => setAskInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAi(); } }}
                          disabled={askBusy}
                        />
                        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askAi()} disabled={askBusy || !askInput.trim()}>
                          {askBusy ? <><span className="cs-spinner" /> …</> : 'Ask'}
                        </button>
                      </div>
                    </div>
                    <div className="cs-workbench-foot">
                      Each slider shows the auto-suggested value — hit <b>Apply</b> (or drag), and the right preview pane updates live
                      on top of every earlier stage. When it looks right, move to the next step.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="cs-studio-side">
            <div>
              <div className="cs-scopes-label">Histogram</div>
              <Scopes stats={stats} />
            </div>
            {compare === 'stage' && (
              <div className="cs-stageflow">
                <div className="cs-stageflow-top">
                  <span className="cs-stageflow-title">Guided grade</span>
                  <div className="cs-stageflow-startbtns">
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      onClick={startFromNegative}
                      title="Reset the working grade to the clean decoded negative and build it back up from scratch"
                    >
                      Start from negative
                    </button>
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      onClick={restoreAiGrade}
                      disabled={!aiGrade}
                      title="Replace the working grade with the model's baked auto-grade"
                    >
                      Restore AI grade
                    </button>
                  </div>
                </div>
                <div className="cs-phasetabs">
                  {PHASES.map((p, pi) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`cs-phasetab ${p.id === stagePhase.id ? 'active' : ''}`}
                      onClick={() => goToStageId(p.steps[0])}
                      title={p.note}
                    >
                      <span className="cs-phasetab-num">{pi + 1}</span>
                      {p.name}
                    </button>
                  ))}
                </div>
                <div className="cs-stageflow-chips">
                  {stagePhase.steps.map((sid) => {
                    const i = STAGES.findIndex((s) => s.id === sid);
                    return (
                      <button
                        key={sid}
                        type="button"
                        className={`cs-chip ${i === stageIdx ? 'active' : ''}`}
                        onClick={() => setStageIdx(i)}
                      >
                        {i + 1}. {STAGES[i].name}
                      </button>
                    );
                  })}
                </div>
                <div className="cs-stageflow-head">
                  <span className="cs-stageflow-sub">
                    Step {stageIdx + 1}/{STAGES.length} · {stagePhase.name}
                  </span>
                  <div className="cs-stageflow-nav">
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-ghost"
                      disabled={stageIdx === 0}
                      onClick={() => setStageIdx((i) => Math.max(0, i - 1))}
                    >
                      ← Prev
                    </button>
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-primary"
                      disabled={stageIdx === STAGES.length - 1}
                      onClick={() => setStageIdx((i) => Math.min(STAGES.length - 1, i + 1))}
                    >
                      Next →
                    </button>
                  </div>
                </div>
                <div className="cs-stageflow-cmp">
                  <span className="cs-stageflow-cmp-lbl">Compare against:</span>
                  <div className="cs-segment cs-segment-sm">
                    <button className={stageTarget === 'prev' ? 'on' : ''} onClick={() => setStageTarget('prev')}>Previous step</button>
                    <button className={stageTarget === 'ai' ? 'on' : ''} onClick={() => setStageTarget('ai')} disabled={!aiGrade}>AI grade</button>
                  </div>
                </div>
                <div className="cs-stageflow-cmp">
                  Left: <b>{stageBeforeLabel}</b> · Right: <b>{stageAfterLabel}</b> · controls below affect only this step.
                </div>
              </div>
            )}
            {compare === 'stage' ? (
              <details className="cs-advanced">
                <summary>All controls (advanced) — tweak any stage</summary>
                <GradePanel
                  grade={grade}
                  onChange={onChange}
                  kind="photo"
                  presets={presets || []}
                  onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'photo' })}
                  onDeletePreset={(id) => deleteGradePreset(id)}
                />
              </details>
            ) : (
              <GradePanel
                grade={grade}
                onChange={onChange}
                kind="photo"
                presets={presets || []}
                onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'photo' })}
                onDeletePreset={(id) => deleteGradePreset(id)}
              />
            )}
            {startLookName && !isNeutral && (
              <div className="cs-startlook">
                <div className="cs-startlook-row">
                  <span className="cs-startlook-tag">Starting look</span>
                  <span className="cs-startlook-name">{startLookName}</span>
                  <span style={{ flex: 1 }} />
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={removeStartingLook} title="Strip the auto-applied look back to the clean neutral base (de-log only)">
                    Remove → neutral base
                  </button>
                </div>
                <div className="cs-startlook-desc">
                  Applied automatically when this frame was grabbed, on top of the neutral base.
                  Edit it with the controls below, or remove it to start from the clean de-logged base.
                </div>
              </div>
            )}
            <ColorWorkflow
              grade={grade}
              currentStageId={stageDef.id}
              onJumpToStep={goToStageId}
              onExportStages={runStageExport}
              stageDerivatives={stageDerivatives}
              baking={baking}
              ready={gradeRender.status === 'ready'}
            />
            {!needsDevelop && (
              <ColorAssistant
                ref={assistantRef}
                grade={grade}
                source={source}
                getCrop={cropGetter}
                onApply={onChange}
                onReframe={applyReframe}
                captureFrames={captureAssistantFrames}
                reference={reference}
                onSetReference={setReference}
                onAutoAlign={autoAlignToReference}
              />
            )}
            <MaskPanel
              masks={masks}
              activeId={activeMaskId}
              maskMode={maskMode}
              onToggleMode={toggleMaskMode}
              onAdd={addMask}
              onAddLinear={addLinearMask}
              onAddAi={addAiMask}
              busyAi={busyAi}
              onSelect={setActiveMaskId}
              onDelete={deleteMask}
              onUpdate={patchMask}
              onUpdateAdj={patchMaskAdj}
            />
          </div>
        </div>
      )}
      {cfgOpen && <GradingConfigPanel onClose={() => setCfgOpen(false)} />}
    </div>
  );
}
